import axios, { AxiosError, AxiosResponse } from 'axios'
import { HEADER_KEYS, HeaderKey } from '@reward-platform/shared-api/headers'
import { Iata, PlatformType, getAviosBasePathEnvValue } from '@reward-platform/platform-config'
import { createDatadogMetaContext, DatadogLogMetaContext } from '@reward-platform/logger/utils'
import { createDatadogLogger } from '~/utils/logger'
import { ApiRouteRequestError } from '~/utils/errors'
import { NEXT_API_URL } from '~/utils/apiConfig'

const initialBasePathValue = getAviosBasePathEnvValue()
export const nextApiClient = axios.create({
  baseURL: initialBasePathValue + NEXT_API_URL,
  timeout: 60000,
  validateStatus(status) {
    return status > 199 && status < 300 // Everything more than 299 will throw a error. Also, if unwanted redirects happen (?)
  },
})

const getLogMetaFromDefaultHeaders = () => {
  const correlationId = nextApiClient.defaults.headers.common[HEADER_KEYS.correlationId]
  const partner = nextApiClient.defaults.headers.common[HEADER_KEYS.partnerCode]
  const platformType = nextApiClient.defaults.headers.common[HEADER_KEYS.platformType]
  return {
    correlationId,
    partner,
    platformType,
  } as DatadogLogMetaContext
}

const getLogContextFromAxiosResponse = (response: AxiosResponse) => {
  return {
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
    http: { ...response.config, method: response.config.method?.toUpperCase() },
  }
}

nextApiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.config.url?.split('?')[0]?.endsWith('/auth/access-token')) {
      return response
    }

    const logger = createDatadogLogger('next-api-response', { level: 'info' })
    const logContext = getLogContextFromAxiosResponse(response)
    const additionalHeaders = getLogMetaFromDefaultHeaders()
    const logMeta = createDatadogMetaContext(additionalHeaders)
    logger.info(`[Browser NextApiResponse] ${response.config.url}`, { ...logContext, ...logMeta })
    return response
  },
  (axiosError: AxiosError) => {
    const logContext = axiosError.response
      ? getLogContextFromAxiosResponse(axiosError.response)
      : {}

    const error = new ApiRouteRequestError(axiosError)
    const additionalHeaders = getLogMetaFromDefaultHeaders()
    error.logToDatadog(additionalHeaders, logContext)
    throw error
  }
)

export const setApiRouteBaseUrl = (basePath?: string) => {
  nextApiClient.defaults.baseURL = basePath ? basePath + NEXT_API_URL : NEXT_API_URL
}

export const setPartnerHeader = (partner: Iata) => {
  nextApiClient.defaults.headers.common[HEADER_KEYS.partnerCode] = partner
}

export const setCorrelationIdHeader = (value: string) => {
  nextApiClient.defaults.headers.common[HEADER_KEYS.correlationId] = value
}

export const setPlatformTypeHeader = (value: PlatformType) => {
  nextApiClient.defaults.headers.common[HEADER_KEYS.platformType] = value
}

export const setClientRequestHeader = (key: HeaderKey, value: string) => {
  const headerKey = HEADER_KEYS[key]
  nextApiClient.defaults.headers.common[headerKey] = value
}

export const getClientRequestHeader = (key: HeaderKey) => {
  const headerKey = HEADER_KEYS[key]
  return nextApiClient.defaults.headers.common[headerKey]
}

export const deleteClientRequestHeader = (key: HeaderKey) => {
  const headerKey = HEADER_KEYS[key]
  delete nextApiClient.defaults.headers.common[headerKey]
}
